<script lang="ts">
    import { getContext, onDestroy, tick } from 'svelte'
    import { derived as sderived } from 'svelte/store'
    import type { Readable } from 'svelte/store'
    import { fade } from 'svelte/transition'

    import Icon from '@iconify/svelte'
    import * as yup from 'yup'

    import { gettext as _ } from 'hydria/services/gettext'
    import { notify } from 'hydria/services/notifications'
    import { simpleFetch, type SimpleFetchResult } from 'hydria/services/session'

    import type { User, UserHandler, UserPasskeyEntry, UserTwofaEntry } from './user'

    import Date from 'hydria/components/Date.svelte'
    import LoadingPattern from 'hydria/components/LoadingPattern.svelte'
    import TwofaLog from './TwofaLog.svelte'

    const userh = getContext('User') as UserHandler

    const status = sderived(userh.status, ($status) => $status)
    const loading = sderived(userh.loading, ($loading) => $loading)

    const user: Readable<User> = sderived(userh.userWritable, ($v) => $v)
    const twofaEntries: Readable<UserTwofaEntry[]> = sderived(userh.twofaEntries, ($v) => $v)
    const passkeyEntries: Readable<UserPasskeyEntry[]> = sderived(userh.passkeyEntries, ($v) => $v)

    const unsubs: any[] = []
    onDestroy(() => {
        for (const unsub of unsubs) unsub.unsubscribe != null ? unsub.unsubscribe() : unsub()
    })

    let working = $state(false)

    let preDelete: UserTwofaEntry | undefined = $state(undefined)
    let preDeletePk: UserPasskeyEntry | undefined = $state(undefined)

    async function doDelete() {
        if (!preDelete) return
        let r: SimpleFetchResult

        working = true

        try {
            r = await simpleFetch('DELETE', `/json/wdom-user/1/${$user.id}/twofa/${preDelete.id}`)
        } catch (e: any) {
            r = e
        }

        if (!r || !r.data || !r.data.success) {
            console.error('Failed to remove twofa entry:', r)
            notify({
                type: 'danger',
                title: 'Authentification',
                text: 'Erreur d’effacement d’une méthode d’authentification.',
            })
            working = false
            return
        }

        preDelete = undefined
        userh.raw.twofa_entries = r.data.entries
        userh.loadTwofaData()

        working = false
    }
    async function doDeletePk() {
        if (!preDeletePk) return
        let r: SimpleFetchResult

        working = true

        try {
            r = await simpleFetch('DELETE', `/json/wdom-user/1/${$user.id}/passkey/${preDeletePk.id}`)
        } catch (e: any) {
            r = e
        }

        if (!r || !r.data || !r.data.success) {
            console.error('Failed to remove passkey entry:', r)
            notify({
                type: 'danger',
                title: 'Authentification',
                text: 'Erreur d’effacement d’une clé d’accès.',
            })
            working = false
            return
        }

        preDeletePk = undefined
        userh.raw.passkey_entries = r.data.entries
        userh.loadTwofaData()

        working = false
    }

    let addType: 'sms' | 'email' | undefined = $state(undefined)
    let focusElement: HTMLInputElement | undefined = $state(undefined)
    let newCode: string = $state('')
    let newComment: string = $state('')

    let showLog = $state(false)

    function showAdd(type: 'sms' | 'email') {
        newCode = type == 'sms' ? '+1.' : ''
        newComment = ''
        addType = type
        tick().then(() => {
            if (focusElement) {
                focusElement.focus()
                // focusElement.select()
            }
        })
    }
    function closeAdd() {
        addType = undefined
    }

    function newCodeIsValid(type: 'sms' | 'email' | undefined, newCode: string) {
        if (type == 'sms') {
            return !!newCode.match(/^\+1\.\d{10}$/)
        }
        if (type == 'email') {
            const newEmailSchema = yup.string().email().optional()
            return newEmailSchema.isValidSync(newCode)
        }

        return false
    }

    async function save() {
        let r: SimpleFetchResult
        working = true

        try {
            r = await simpleFetch('PUT', `/json/wdom-user/1/${$user.id}/twofa`, {
                type: addType,
                code: newCode,
                comment: newComment,
            })
        } catch (e: any) {
            r = e
        }

        if (!r || !r.data || !r.data.success) {
            console.error('Failed to remove twofa entry:', r)
            notify({
                type: 'danger',
                title: '2FA',
                text: 'Erreur d’ajout d’une méthode d’authentification.',
            })
            working = false
            return
        }

        closeAdd()
        userh.raw.twofa_entries = r.data.entries
        userh.loadTwofaData()

        working = false
    }

    let allowedWorking = $state(false)
    async function setConfigAllowed(nv: boolean) {
        allowedWorking = true

        try {
            const r = await simpleFetch(nv ? 'PUT' : 'DELETE', `/json/wdom-user/1/${$user.id}/security-configuration-allowed`)
            userh.raw.item.security_configuration_allowed = r.data.security_configuration_allowed
            userh.loadUserResponse()

            notify({
                type: 'success',
                title: 'User actif',
                text: 'L’accès aux modifications dans la section S´´curité est maintenant ' + (nv ? 'permis' : 'proscrit') + '.',
            })
        } catch (e) {
            console.error('Failed to set active state:', e)
            notify({
                type: 'danger',
                title: 'User actif',
                text: 'L’état « security_configuration_allowed » n’a pas pu être changé',
            })
        }

        allowedWorking = false
    }

    let signinNotifWorking = $state(false)
    async function setSigninNotifications(nv: boolean) {
        signinNotifWorking = true

        try {
            const r = await simpleFetch(nv ? 'PUT' : 'DELETE', `/json/wdom-user/1/${$user.id}/signin-notifications`)
            userh.raw.item.signin_notifications = r.data.signin_notifications
            userh.loadUserResponse()

            // notify({
            //     type: 'success',
            //     title: 'Notifications de connexion',
            //     text: 'L’accès aux modifications dans la section S´´curité est maintenant ' + (nv ? 'permis' : 'proscrit') + '.',
            // })
        } catch (e) {
            console.error('Failed to set active state:', e)
            notify({
                type: 'danger',
                title: 'Notifications de connexion',
                text: 'L’état « signin_notifications » n’a pas pu être changé',
            })
        }

        signinNotifWorking = false
    }

    $effect(() => {
        if (addType && !document.documentElement.classList.contains('modal-open')) {
            document.documentElement.classList.add('modal-open')
        } else if (!addType && document.documentElement.classList.contains('modal-open')) {
            document.documentElement.classList.remove('modal-open')
        }
    })

    onDestroy(() => {
        if (addType && document.documentElement.classList.contains('modal-open')) {
            document.documentElement.classList.remove('modal-open')
        }
    })
</script>

<style>
    .pkentry {
        display: grid;
        grid-template-columns: 2fr 3fr 5.25em;
        grid-template-rows: auto auto;
        grid-template-areas: 'ctime code trash';
        gap: 0 0.5em;
        align-items: center;
    }
    .entry {
        display: grid;
        grid-template-columns: 1fr 3fr 5.25em;
        grid-template-rows: auto auto;
        grid-template-areas: 'type code trash';
        gap: 0 0.5em;
        align-items: center;
    }
    .nope {
        grid-template-columns: 1fr;
        grid-template-rows: 2.5em;
        grid-template-areas: 'code';
        align-items: center;
        justify-items: center;
        color: #999;
    }
    .entry:not(.entry-header),
    .pkentry:not(.entry-header) {
        padding: 0.25em 0;
    }
    .entry:not(:last-child):not(.entry-header),
    .pkentry:not(:last-child):not(.entry-header) {
        border-bottom: 1px solid #eee;
    }
    .entry:last-child,
    .pkentry:last-child,
    .entry-header {
        border-bottom: 1px solid #999;
    }

    .entry.has-cmt {
        grid-template-areas: 'type code trash' 'type cmt trash';
    }
    .entry:not(.has-cmt) .cmt {
        display: none;
    }
    .entry.has-cmt .cmt {
        display: initial;
    }
    .type {
        grid-area: type;
    }
    .ctime {
        grid-area: ctime;
    }
    .code {
        grid-area: code;
        font-feature-settings:
            'ss01' 1,
            'tnum' 1;
    }
    .cmt {
        grid-area: cmt;
        color: #999;
        font-size: 0.9em;
    }
    .trash {
        grid-area: trash;
        justify-self: end;
    }

    .entry.entry-header,
    .pkentry.entry-header {
        font-size: 0.8em;
        font-variant: small-caps;
        color: #999;
        padding-bottom: 0.25em;
        gap: 0 0.5em;
    }
</style>

<svelte:window
    onkeydown={(e) => {
        if (e.key == 'Escape') {
            if (addType) {
                addType = undefined
            }
        }
    }}
/>

<div class="card">
    <div class="card-title text-primary">{$_('Authentification')}</div>
    {#if $status == 'loading' || $loading}
        <div class="card-body">
            <LoadingPattern pattern={[20, 30, 30, 30, 30]} />
        </div>
    {:else}
        <div class="card-body">
            <div class="mb-2 text-center"><strong>{$_('Clés d’accès')}</strong></div>
            <div class="pkentry entry-header">
                <span class="ctime">Date</span>
                <span class="code">Nom</span>
                <span class="trash"><Icon inline icon="mdi:trash" /></span>
            </div>
            {#if $passkeyEntries.length < 1}
                <div class="pkentry nope">
                    <span class="code">Aucune entrée</span>
                </div>
            {/if}
            {#each $passkeyEntries as entry}
                <div class="pkentry">
                    <span class="ctime"><Date value={entry.ctime} format="h-table-s" /></span>
                    <span class="code"
                        >{#if entry.name}{entry.name}{:else}<span class="text-muted">Sans nom</span>{/if}</span
                    >
                    <span class="trash">
                        {#if preDeletePk && preDeletePk.id == entry.id}
                            <button
                                type="button"
                                disabled={working}
                                class="btn btn-sm btn-outline-danger"
                                onclick={() => {
                                    doDeletePk()
                                }}
                            >
                                <Icon inline icon="mdi:trash" />
                            </button>
                            <button
                                type="button"
                                disabled={working}
                                class="btn btn-sm btn-outline-secondary"
                                onclick={() => {
                                    preDeletePk = undefined
                                }}
                            >
                                <Icon inline icon="mdi:cancel" />
                            </button>
                        {:else}
                            <button
                                type="button"
                                disabled={working}
                                class="btn btn-sm btn-outline-warning"
                                onclick={() => {
                                    preDeletePk = entry
                                }}
                            >
                                <Icon inline icon="mdi:trash" />
                            </button>
                        {/if}
                    </span>
                </div>
            {/each}

            <div class="mt-3 mb-2 text-center"><strong>{$_('Second Facteur')}</strong></div>
            <div class="entry entry-header">
                <span class="type">Type</span>
                <span class="code">Code</span>
                <span class="trash"><Icon inline icon="mdi:trash" /></span>
            </div>
            {#if $twofaEntries.length < 1}
                <div class="entry nope">
                    <span class="code">Aucune entrée</span>
                </div>
            {/if}
            {#each $twofaEntries as entry}
                <div class="entry" class:has-cmt={entry['comment']}>
                    {#if entry['type'] == 'totp'}
                        <span class="type"><Icon inline icon="mdi:cellphone" /> App</span>
                        <span class="code"></span>
                        <span class="cmt"></span>
                    {:else if entry['type'] == 'sms'}
                        <span class="type"><Icon inline icon="mdi:message-text" /> SMS</span>
                        <span class="code">{entry['code']}</span>
                        <span class="cmt">{entry['comment']}</span>
                    {:else if entry['type'] == 'email'}
                        <span class="type"><Icon inline icon="mdi:email" /> Courriel</span>
                        <span class="code">{entry['code'] ? entry['code'] : '<courriel du compte>'}</span>
                        <span class="cmt">{entry['comment']}</span>
                    {:else}
                        <span class="type"><Icon inline icon="mdi:question-mark" /> {entry['type']} ?</span>
                        <span class="code">{entry['code']}</span>
                        <span class="cmt">{entry['comment']}</span>
                    {/if}
                    <span class="trash">
                        {#if preDelete && preDelete.id == entry.id}
                            <button
                                type="button"
                                disabled={working}
                                class="btn btn-sm btn-outline-danger"
                                onclick={() => {
                                    doDelete()
                                }}
                            >
                                <Icon inline icon="mdi:trash" />
                            </button>
                            <button
                                type="button"
                                disabled={working}
                                class="btn btn-sm btn-outline-secondary"
                                onclick={() => {
                                    preDelete = undefined
                                }}
                            >
                                <Icon inline icon="mdi:cancel" />
                            </button>
                        {:else}
                            <button
                                type="button"
                                disabled={working}
                                class="btn btn-sm btn-outline-warning"
                                onclick={() => {
                                    preDelete = entry
                                }}
                            >
                                <Icon inline icon="mdi:trash" />
                            </button>
                        {/if}
                    </span>
                </div>
            {/each}
        </div>
        <div class="card-body" style="display: flex; flex-direction: column; gap: 0.5em 0;">
            <div>
                {#if allowedWorking}
                    <div class="spinner-border spinner-border-sm" role="status" aria-hidden="true"></div>
                    {#if $user.security_configuration_allowed}{$_('Configuration de la section Sécurité en lecture seule…')}
                    {:else}{$_('Configuration de la section Sécurité en plein accès…')}
                    {/if}
                {:else if $user.security_configuration_allowed}
                    {$_('L’utilisateur a plein accès à la section Sécurité.')}<br />
                    <button class="btn btn-link btn-a" type="button" onclick={() => setConfigAllowed(false)}>
                        {$_('Limiter à lecture seule')}
                    </button>
                {:else}
                    {$_('L’utilisateur a accès à la section Sécurité uniquement en lecture.')}<br />
                    <button class="btn btn-link btn-a" type="button" onclick={() => setConfigAllowed(true)}>
                        {$_('Donner plein accès')}
                    </button>
                {/if}
            </div>
            <div>
                {#if signinNotifWorking}
                    <div class="spinner-border spinner-border-sm" role="status" aria-hidden="true"></div>
                    {$_('Configuration des notifications…')}
                {:else if $user.signin_notifications}
                    {$_('L’utilisateur reçoit un courriel à chaque connexion.')}<br />
                    <button class="btn btn-link btn-a" type="button" onclick={() => setSigninNotifications(false)}>
                        {$_('Désactiver')}
                    </button>
                {:else}
                    {$_('L’utilisateur ne reçoit pas de courriel sur connexion à son compte.')}<br />
                    <button class="btn btn-link btn-a" type="button" onclick={() => setSigninNotifications(true)}>
                        {$_('Activer ces notifications')}
                    </button>
                {/if}
            </div>
        </div>
        <div class="card-footer text-bg-light d-flex gap-2 justify-content-end">
            <button
                type="button"
                class="btn btn-outline-secondary"
                disabled={working}
                onclick={() => {
                    showLog = true
                }}
            >
                <Icon inline icon="mdi:journal" /> Journal
            </button>

            <!-- <button type="button" class="btn btn-outline-secondary" disabled={true}>
                <Icon inline icon="mdi:cellphone" /> Authentificateur
            </button> -->
            <button
                type="button"
                class="btn btn-outline-secondary"
                disabled={working}
                onclick={() => {
                    showAdd('sms')
                }}
            >
                <Icon inline icon="mdi:message-text" /> SMS
            </button>
            <button
                type="button"
                class="btn btn-outline-secondary"
                disabled={working}
                onclick={() => {
                    showAdd('email')
                }}
            >
                <Icon inline icon="mdi:email" /> Courriel
            </button>
        </div>
    {/if}
</div>

{#if addType}
    <div class="modal" role="dialog" transition:fade={{ duration: 250 }}>
        <div class="modal-dialog modal-lg">
            <div class="modal-content">
                <div class="modal-header text-bg-primary">
                    <div>
                        {#if addType == 'sms'}
                            Code par SMS
                        {:else}
                            Code par courriel
                        {/if}
                    </div>
                </div>
                <div class="modal-body">
                    {#if addType == 'sms'}
                        <p>Format: <code>+1.xxxyyyzzzz</code> :</p>
                        <p><input type="text" class="form-control" bind:this={focusElement} bind:value={newCode} /></p>
                    {:else}
                        <p>Adresse email, <em>laisser vide</em> pour utiliser l’adresse du compte :</p>
                        <p><input type="text" class="form-control" bind:this={focusElement} bind:value={newCode} /></p>
                    {/if}
                    <p>Commentaire optionnel :</p>
                    <p><input type="text" class="form-control" bind:value={newComment} /></p>
                </div>
                <div class="modal-footer text-bg-light">
                    <button
                        type="button"
                        class="btn btn-outline-secondary"
                        onclick={() => {
                            closeAdd()
                        }}>{$_('Cancel')}</button
                    >
                    <button
                        type="button"
                        class="btn btn-primary"
                        onclick={() => {
                            save()
                        }}
                        disabled={!newCodeIsValid(addType, newCode)}
                    >
                        {#if working}<div class="spinner-border spinner-border-sm" role="status" aria-hidden="true"></div>
                            {$_('Saving…')}
                        {:else}{$_('Save')}
                        {/if}
                    </button>
                </div>
            </div>
        </div>
        <div
            class="modal-backdrop"
            onclick={() => {
                closeAdd()
            }}
            aria-hidden="true"
        ></div>
    </div>
{/if}

{#if showLog}
    <TwofaLog
        close={() => {
            showLog = false
        }}
    />
{/if}
